// src/components/quotes/quoteCover.js
//
// THE QUOTE'S OPTIONAL COVER PAGE, and the storage rule that makes it safe.
//
// ── ⚠ THE IMAGE IS NEVER STORED INLINE. IT IS A KEYED MAP + A POINTER ──────
//     quote.coverImages = { [imgId]: dataURL }    stored once
//     quote.coverImageId = imgId                  a pointer, never the image
//
// This is not a style preference. CLAUDE.md records the measurement that forced
// it on proposals: inline base64 covers came to 21–43MB across 20 proposals,
// against a 50MB express.json ceiling **where a rejected save surfaces only as a
// console.warn** — i.e. the save fails and nothing tells anyone. Quotes live in
// the SAME app-state blob as proposals, so an inline quote cover does not just
// risk its own quote; it pushes the shared blob toward a ceiling that fails
// silently for everything in it.
//
// ── HOW THIS DIFFERS FROM PROPOSALS, AND WHY ───────────────────────────────
// A proposal keeps SNAPSHOTS, which deep-clone `content`, so its prune must
// keep every image any snapshot still points at, and changing the cover mints a
// new id so old snapshots keep resolving the image they were taken with.
//
// A QUOTE HAS NO SNAPSHOTS — there is no `snapshots` array on the record. So
// nothing can reference a superseded image, and the prune keeps exactly one.
// A fresh id is still minted per upload: it costs nothing, and it means a
// stale reference resolves to nothing rather than to the wrong picture if
// snapshots are ever added.

/** A new image id. Time plus randomness — two uploads in one millisecond are
 *  possible if a picker fires twice, and a collision would overwrite. */
function newImageId() {
  return `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * The patch to apply when the cover image changes.
 *
 * @param {object} quote    the current quote (read for its existing map)
 * @param {string|null} dataUrl  the new image, or null/"" to remove it
 * @returns {object} a patch for updateQuote
 *
 * ⚠ RETURNS BOTH FIELDS ALWAYS. Setting `coverImageId` without rewriting
 * `coverImages` is how the map grows without bound — re-pick a cover five times
 * and the quote carries five full images, four of them unreachable.
 */
export function quoteCoverPatch(quote, dataUrl) {
  if (!dataUrl) {
    // Removing the image empties the map: with no snapshots, nothing else can
    // be pointing at what was there.
    return { coverImageId: null, coverImages: {} };
  }
  const imgId = newImageId();
  return { coverImageId: imgId, coverImages: { [imgId]: dataUrl } };
}

/** The cover image itself, or null. Resolved through the pointer, never read
 *  from a field on the quote — there is no such field, deliberately. */
export function coverImageOf(quote) {
  const id = quote?.coverImageId;
  return (id && quote?.coverImages?.[id]) || null;
}

/**
 * Is the cover page part of this document?
 *
 * ⚠ OFF UNLESS EXPLICITLY ON. `!!` on an absent flag, so every quote written
 * before covers existed renders exactly as it did — no blank first page, no
 * migration. And a two-line "supply and install three switches" quote is not
 * made to carry a photographic cover it never asked for.
 */
export function coverEnabled(quote) {
  return !!quote?.coverEnabled;
}

/**
 * Roughly how many bytes this quote's cover adds to the shared state blob.
 * Surfaced in the UI beside the thumbnail — the ceiling that bites here fails
 * SILENTLY, so the one defence a person has is being able to see the weight.
 * base64 is ~4 bytes per 3, and the data: prefix is negligible.
 */
export function coverImageBytes(quote) {
  const img = coverImageOf(quote);
  return img ? Math.round((img.length * 3) / 4) : 0;
}
